import React from "react";
import { Box, Typography, IconButton, Tooltip, Chip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import StatusChip from "../common/StatusChip";

const EmployeesDataGrid = ({ rows, loading, onEdit, onDetail, onDeactivate, onReactivate, onCreateUser }) => {
  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 1.5,
      minWidth: 200,
      // valueGetter hace que DataGrid pueda ordenar/filtrar por el nombre
      // completo (el backend no trae fullName — solo firstName/lastName).
      valueGetter: (_, row) =>
        `${row?.firstName || ""} ${row?.lastName || ""}`.trim(),
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {row.firstName} {row.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.businessEmail || row.mail || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "jobPositionName",
      headerName: "Job Position",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "admission",
      headerName: "Admission Date",
      width: 130,
      renderCell: ({ value }) =>
        value
          ? new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "—",
    },
    {
      field: "activeLocations",
      headerName: "Locations",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hasUser",
      headerName: "User Account",
      width: 150,
      renderCell: ({ row }) =>
        row.hasUser ? (
          <Chip label="Has account" size="small" color="success" variant="outlined" />
        ) : row.status === 1 ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => onCreateUser(row)}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Create User
          </Button>
        ) : (
          <Chip label="No account" size="small" variant="outlined" color="default" />
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ value }) => <StatusChip active={value === 1} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Detail">
            <IconButton size="small" onClick={() => onDetail(row)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 1 ? (
            <Tooltip title="Deactivate">
              <IconButton size="small" color="error" onClick={() => onDeactivate(row)}>
                <PersonOffOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate">
              <IconButton size="small" color="success" onClick={() => onReactivate(row)}>
                <PersonOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.employeeId}
      loading={loading}
      autoHeight
      pageSizeOptions={[25, 50, 100]}
      initialState={{
        pagination: { paginationModel: { pageSize: 100 } },
        sorting: { sortModel: [{ field: "fullName", sort: "asc" }] },
      }}
      disableRowSelectionOnClick
      density="comfortable"
      sx={{ border: "none" }}
    />
  );
};

export default EmployeesDataGrid;
